import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicFooter } from "@/components/common/PublicFooter";
import { PublicNav } from "@/components/common/PublicNav";
import { useGuest } from "@/store/guest";
import { useSession } from "@/store/session";

const SUGGESTIONS = [
  "What does the Quran say about prayer?",
  "How is charity described?",
  "What is the meaning of Taqwa?",
];

export function Landing() {
  const navigate = useNavigate();
  const isSessionValid = useSession((s) => s.isValid);
  const resetGuest = useGuest((s) => s.reset);
  const [q, setQ] = useState("");

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text) return;
    if (isSessionValid()) {
      // Navigate to /chat and hand the question off via location.state so ChatPage
      // can auto-send it. Server will create a conversation and echo the id back.
      navigate("/chat", { state: { pendingQuestion: text } });
    } else {
      resetGuest();
      navigate("/chat/guest", { state: { pendingQuestion: text } });
    }
  };

  return (
    <div className="sa-page" style={{ display: "flex", flexDirection: "column" }}>
      <PublicNav />

      <section
        className="sa-landing-hero"
        style={{
          position: "relative",
          padding: "120px 56px 96px",
          textAlign: "center",
          overflow: "hidden",
          borderBottom: "1px solid var(--sa-border)",
        }}
      >
        <div
          className="sa-landing-hero__inner"
          style={{ position: "relative", maxWidth: 820, margin: "0 auto" }}
        >
          <div
            className="sa-label"
            style={{
              marginBottom: 28,
              display: "inline-block",
              borderTop: "1px solid var(--sa-border-strong)",
              paddingTop: 10,
            }}
          >
            A Research Instrument · Est. 2026
          </div>
          <h1
            className="sa-landing-hero__title"
            style={{
              fontFamily: "var(--sa-serif)",
              fontWeight: 400,
              fontSize: 68,
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              margin: "0 0 24px",
              fontStyle: "italic",
            }}
          >
            Answers from the
            <br />
            Quran <span style={{ fontStyle: "normal", fontWeight: 500 }}>alone</span>.
          </h1>
          <p
            className="sa-landing-hero__lede"
            style={{
              fontSize: 19,
              lineHeight: 1.55,
              color: "var(--sa-muted-fg)",
              maxWidth: 560,
              margin: "0 auto 48px",
              fontFamily: "var(--sa-serif)",
            }}
          >
            A conversational instrument for studying the Quran. Every response is grounded
            in scripture and cites its sources — verse, chapter, line.
          </p>

          <form
            className="sa-landing-hero__form"
            onSubmit={submit}
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 0,
              maxWidth: 640,
              margin: "0 auto",
              border: "1px solid var(--sa-border-strong)",
              background: "var(--sa-bg-pure)",
              borderRadius: 2,
              boxShadow: "0 1px 0 0 var(--sa-border)",
            }}
          >
            <input
              className="sa-landing-hero__input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask a question…"
              maxLength={2000}
              aria-label="Ask a question"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                padding: "20px 24px",
                fontFamily: "var(--sa-serif)",
                fontSize: 18,
                background: "transparent",
                color: "var(--sa-fg)",
              }}
            />
            <button
              type="submit"
              className="sa-btn"
              style={{ borderRadius: 0, padding: "0 28px", fontSize: 13, flexShrink: 0 }}
              disabled={!q.trim()}
            >
              Ask →
            </button>
          </form>

          <div
            style={{
              marginTop: 20,
              fontFamily: "var(--sa-sans)",
              fontSize: 12,
              color: "var(--sa-muted-fg)",
              letterSpacing: "0.02em",
            }}
          >
            No account needed to try.{" "}
            <span style={{ color: "var(--sa-muted-fg)" }}>
              Guest chats never leave your browser.
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              flexWrap: "wrap",
              marginTop: 40,
            }}
          >
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setQ(s)}
                style={{
                  fontFamily: "var(--sa-serif)",
                  fontStyle: "italic",
                  fontSize: 14,
                  padding: "8px 14px",
                  border: "1px solid var(--sa-border)",
                  background: "var(--sa-bg-pure)",
                  color: "var(--sa-muted-fg)",
                  cursor: "pointer",
                  borderRadius: 2,
                }}
              >
                “{s}”
              </button>
            ))}
          </div>
        </div>
      </section>

      <section
        className="sa-landing-principles"
        style={{ padding: "96px 56px", maxWidth: 1200, margin: "0 auto", width: "100%" }}
      >
        <div className="sa-label" style={{ marginBottom: 48, textAlign: "center" }}>
          — Three Principles —
        </div>
        <div
          className="sa-landing-principles__grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 1,
            background: "var(--sa-border)",
          }}
        >
          {[
            {
              n: "I.",
              title: "Grounded in the Quran",
              body:
                "No Hadith. No Sunnah. No scholarly opinion dressed as scripture. Every answer draws exclusively from the Quranic corpus.",
            },
            {
              n: "II.",
              title: "Cites every source",
              body:
                "Verses are attributed by chapter and line. You may click any citation to view the surrounding passage in its original context.",
            },
            {
              n: "III.",
              title: "Multi-turn memory",
              body:
                "Your signed-in conversations are preserved across sessions. Return to any inquiry and extend it further, as a scholar keeps notes.",
            },
          ].map((f) => (
            <div
              key={f.n}
              style={{ background: "var(--sa-bg)", padding: "48px 36px", minHeight: 260 }}
            >
              <div
                style={{
                  fontFamily: "var(--sa-serif)",
                  fontStyle: "italic",
                  fontSize: 14,
                  color: "var(--sa-accent)",
                  marginBottom: 18,
                }}
              >
                {f.n}
              </div>
              <h3
                style={{
                  fontFamily: "var(--sa-serif)",
                  fontWeight: 500,
                  fontSize: 22,
                  margin: "0 0 14px",
                  letterSpacing: "-0.01em",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.65,
                  color: "var(--sa-muted-fg)",
                }}
              >
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section
        className="sa-landing-quote"
        style={{
          padding: "80px 56px 120px",
          textAlign: "center",
          borderTop: "1px solid var(--sa-border)",
          background: "var(--sa-muted)",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div
            style={{
              fontFamily: "var(--sa-mono)",
              fontSize: 12,
              color: "var(--sa-muted-fg)",
              letterSpacing: "0.08em",
              marginBottom: 18,
            }}
          >
            [ 29:45 ]
          </div>
          <blockquote
            style={{
              fontFamily: "var(--sa-serif)",
              fontStyle: "italic",
              fontSize: 26,
              lineHeight: 1.5,
              margin: 0,
              color: "var(--sa-fg)",
              fontWeight: 400,
            }}
          >
            “The Contact Prayers prohibit evil and vice; and the remembrance of GOD through
            Salat is the most important objective.”
          </blockquote>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
